import React, { useContext, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import LogoutIcon from "../icons/LogoutIcon";
import ActionConfirmationDialog from "../../../components/ActionConfirmationDialog";
import { AppLayoutContext } from "../../../contexts/AppLayoutContext";
import { useRewampLocalization, useRewampT } from "../../localisation";
import { PWA_DRAWER } from "../../layouts/pwaConfig";
import { nativeShare } from "../assets/bankRegistryShare";
import ShareBankDetailsDialog from "../assets/ShareBankDetailsDialog";
import PwaProfileAvatar from "./PwaProfileAvatar";

export const PWA_PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.dfs.biharkrishi&hl=en_IN";

const noop = () => {};

const ProfileBlock = ({ onViewProfile }) => {
  const tRewamp = useRewampT();
  const profile = useSelector((state) => state.profileData) || {};
  const fullName = profile.name?.trim() || tRewamp("PWA_ACCOUNT_DRAWER.NAME_FALLBACK");

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <PwaProfileAvatar photoId={profile.photo} />
      <Box
        sx={{
          mt: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          minWidth: 0,
        }}
      >
        <Typography variant="drawerUserName" noWrap sx={{ flex: 1, minWidth: 0, color: "neutral.headings" }}>
          {fullName}
        </Typography>
        <Button variant="link" onClick={onViewProfile} sx={{ flexShrink: 0 }}>
          {tRewamp("PWA_ACCOUNT_DRAWER.VIEW_PROFILE")}
        </Button>
      </Box>
      <Box
        sx={{
          mt: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Typography variant="body1" component="span" sx={{ color: "neutral.navInactive" }}>
          {tRewamp("PWA_ACCOUNT_DRAWER.MOBILE_LABEL")}
        </Typography>
        <Typography variant="statValue" sx={{ color: "neutral.headings", textAlign: "right" }}>
          {profile.mobileNumber || "-"}
        </Typography>
      </Box>
    </Box>
  );
};

ProfileBlock.propTypes = { onViewProfile: PropTypes.func.isRequired };

const ShareBlock = () => {
  const { t } = useTranslation();
  const tRewamp = useRewampT();
  const [fallbackOpen, setFallbackOpen] = useState(false);
  const title = tRewamp("PWA_ACCOUNT_DRAWER.SHARE_TITLE");

  const handleShare = async () => {
    const result = await nativeShare({ text: PWA_PLAY_STORE_URL });
    if (result === "unavailable" || result === "failed") setFallbackOpen(true);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box
          component="img"
          src={`${process.env.PUBLIC_URL}/assets/RewampBiharKrishiLogo.png`}
          alt={t("BIHAR_KRISHI_LOGO")}
          sx={{ width: (theme) => theme.spacing(5.5), height: (theme) => theme.spacing(4), objectFit: "contain", flexShrink: 0 }}
        />
        <Typography variant="subsectionTitle" sx={{ color: "neutral.headings" }}>
          {title}
        </Typography>
      </Box>
      <Card variant="tintCard" sx={{ bgcolor: "neutral.legacySeedFormBg" }}>
        <Typography variant="body1" sx={{ color: "neutral.body" }}>
          {tRewamp("PWA_ACCOUNT_DRAWER.SHARE_BODY")}
        </Typography>
        <Button variant="link" onClick={handleShare} sx={{ alignSelf: "flex-start" }}>
          {tRewamp("PWA_ACCOUNT_DRAWER.SHARE_LINK")}
        </Button>
        <ShareBankDetailsDialog
          shareText={PWA_PLAY_STORE_URL}
          subject={title}
          open={fallbackOpen}
          onClose={() => setFallbackOpen(false)}
        />
      </Card>
    </Box>
  );
};

const PwaAccountDrawer = ({ open, onClose }) => {
  useRewampLocalization(["common"]);
  const tRewamp = useRewampT();
  const navigate = useNavigate();
  const { handleLogout } = useContext(AppLayoutContext) || {};
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const confirmLogout = async () => {
    setLogoutOpen(false);
    setLoggingOut(true);
    try {
      await Promise.resolve(handleLogout?.());
    } finally {
      if (mountedRef.current) setLoggingOut(false);
    }
  };

  const handleViewProfile = () => {
    onClose();
    navigate(`${window.contextPath}/edit-profile`);
  };

  const divider = <Divider sx={{ my: 2.5, borderColor: "neutral.searchStroke" }} />;

  return (
    <SwipeableDrawer
      anchor="left"
      open={open}
      onClose={onClose}
      onOpen={noop}
      disableSwipeToOpen
      PaperProps={{
        role: "dialog",
        "aria-modal": true,
        "aria-label": tRewamp("PWA_APPBAR.PROFILE_MENU"),
        sx: {
          width: PWA_DRAWER.width,
          bgcolor: "background.paper",
          borderRadius: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          pb: 1.5,
        },
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "flex-end", px: 2, pt: 2, flexShrink: 0 }}>
        <IconButton size="touch" edge="end" aria-label={tRewamp("PWA_ACCOUNT_DRAWER.CLOSE_ARIA")} onClick={onClose}>
          <CloseIcon aria-hidden="true" />
        </IconButton>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 2 }}>
        <ProfileBlock onViewProfile={handleViewProfile} />
        {divider}
        <Button
          variant="dangerText"
          disabled={loggingOut}
          aria-busy={loggingOut ? "true" : undefined}
          startIcon={
            loggingOut ? (
              <CircularProgress size="1em" color="inherit" />
            ) : (
              <LogoutIcon aria-hidden="true" sx={{ fontSize: (theme) => theme.spacing(3), color: "error.main" }} />
            )
          }
          onClick={() => setLogoutOpen(true)}
        >
          {tRewamp("PWA_ACCOUNT_DRAWER.LOGOUT")}
        </Button>
        {divider}
        <ShareBlock />
      </Box>
      <ActionConfirmationDialog
        open={logoutOpen}
        setOpen={setLogoutOpen}
        onContinueClick={confirmLogout}
        actionHeader="COMMON_LOGOUT"
        warningText="COMMON_LOGOUT_WARNING"
      />
    </SwipeableDrawer>
  );
};

PwaAccountDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default PwaAccountDrawer;
